import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Button, Alert, CircularProgress, Paper,
  Drawer, useMediaQuery
} from '@mui/material';
import { Bot, Send, Trash2, Plus, MessageSquare, PanelLeftOpen, X } from 'lucide-react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import NexusWidgets from './NexusWidgets';
import NexusToolCallLog from './NexusToolCallLog';
import ChatMarkdown from './ChatMarkdown';

export default function NexusChat({ getToken, initialContext, onClose }) {
  const { activeTeam } = useTeam();
  const teamId = activeTeam?.id;
  const isMobile = useMediaQuery('(max-width:600px)');

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Messages
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load sessions
  const loadSessions = useCallback(async () => {
    if (!teamId) return;
    try {
      setSessionsLoading(true);
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/nexus/${teamId}/sessions`, { headers: h });
      if (!res.ok) throw new Error('Failed to load sessions');
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setSessionsLoading(false);
    }
  }, [teamId, getToken]);

  // Create new session
  const createSession = useCallback(async () => {
    if (!teamId) return;
    try {
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/nexus/${teamId}/sessions`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to create session');
      const data = await res.json();
      const newSession = data.session;
      setSessions((prev) => [newSession, ...prev]);
      setActiveSessionId(newSession.sessionId);
      setMessages([]);
      setSidebarOpen(false);
    } catch (e) {
      setError(e.message);
    }
  }, [teamId, getToken]);

  // Delete session
  const deleteSession = useCallback(async (sessionId) => {
    if (!teamId) return;
    try {
      const h = await getAuthHeaders(getToken);
      await fetch(`${API_BASE_URL}/api/nexus/${teamId}/sessions/${sessionId}`, {
        method: 'DELETE', headers: h
      });
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [teamId, getToken, activeSessionId]);

  // Load history for active session
  const loadHistory = useCallback(async () => {
    if (!teamId || !activeSessionId) {
      setMessages([]);
      return;
    }
    try {
      setLoading(true);
      const h = await getAuthHeaders(getToken);
      const res = await fetch(`${API_BASE_URL}/api/nexus/${teamId}/history?limit=50&sessionId=${activeSessionId}`, { headers: h });
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, getToken, activeSessionId]);

  // Initial load
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-select first session or create one
  useEffect(() => {
    if (sessions.length > 0 && !activeSessionId) {
      setActiveSessionId(sessions[0].sessionId);
    }
  }, [sessions, activeSessionId]);

  // Load history when session changes
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { scrollToBottom(); }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    // Auto-create session if none exists
    let sid = activeSessionId;
    if (!sid) {
      try {
        const h = await getAuthHeaders(getToken);
        const res = await fetch(`${API_BASE_URL}/api/nexus/${teamId}/sessions`, {
          method: 'POST',
          headers: { ...h, 'Content-Type': 'application/json' }
        });
        if (!res.ok) throw new Error('Failed to create session');
        const data = await res.json();
        const newSession = data.session;
        setSessions((prev) => [newSession, ...prev]);
        setActiveSessionId(newSession.sessionId);
        sid = newSession.sessionId;
      } catch (e) {
        setError(e.message);
        return;
      }
    }

    setInput('');
    setSending(true);
    setError('');

    // Optimistic UI
    const userMsg = { role: 'user', content: text, senderUsername: '', createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const h = await getAuthHeaders(getToken);
      const body = { message: text, sessionId: sid };
      if (initialContext) body.context = initialContext;

      const res = await fetch(`${API_BASE_URL}/api/nexus/${teamId}/chat`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Failed');
      }

      const data = await res.json();
      const assistantMsg = {
        role: 'assistant',
        content: data.text || '',
        widgets: data.widgets || null,
        toolCalls: data.toolCalls || null,
        createdAt: new Date().toISOString()
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Update session title if AI provided one
      if (data.chatTitle && sid) {
        setSessions((prev) => prev.map((s) =>
          s.sessionId === sid ? { ...s, title: data.chatTitle, updatedAt: new Date().toISOString() } : s
        ));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const switchSession = (sessionId) => {
    if (sessionId === activeSessionId) return;
    setActiveSessionId(sessionId);
    setSidebarOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Sidebar content (shared between mobile drawer and desktop sidebar)
  const sidebarContent = (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      {/* New Chat button */}
      <Box px={1} py={1} flexShrink={0}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<Plus size={14} />}
          onClick={createSession}
          sx={{
            borderColor: 'rgba(139,92,246,0.3)',
            color: '#a78bfa',
            justifyContent: 'flex-start',
            fontSize: '0.8rem',
            '&:hover': { borderColor: 'rgba(139,92,246,0.5)', bgcolor: 'rgba(139,92,246,0.08)' }
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Session list */}
      <Box flex={1} minHeight={0} overflow="auto" className="vr-scroll-y" px={0.5}>
        {sessionsLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={18} />
          </Box>
        )}
        {sessions.map((s) => (
          <Box
            key={s.sessionId}
            onClick={() => switchSession(s.sessionId)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.75,
              borderRadius: '6px',
              cursor: 'pointer',
              bgcolor: s.sessionId === activeSessionId ? 'rgba(139,92,246,0.12)' : 'transparent',
              '&:hover': { bgcolor: s.sessionId === activeSessionId ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.04)' },
              transition: 'background 0.15s'
            }}
          >
            <MessageSquare size={14} style={{ color: s.sessionId === activeSessionId ? '#a78bfa' : 'rgba(161,161,170,0.6)', flexShrink: 0 }} />
            <Typography
              noWrap
              sx={{
                flex: 1,
                fontSize: '0.8rem',
                fontWeight: s.sessionId === activeSessionId ? 600 : 400,
                color: s.sessionId === activeSessionId ? '#a78bfa' : 'text.secondary'
              }}
            >
              {s.title || 'New Chat'}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); deleteSession(s.sessionId); }}
              sx={{
                p: 0.25,
                color: 'text.disabled',
                opacity: 0,
                '.MuiBox-root:hover > &': { opacity: 1 },
                '&:hover': { color: 'error.main' }
              }}
            >
              <Trash2 size={13} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box display="flex" height="100%" minHeight={0}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: 200,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Mobile sidebar drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: {
              width: 260,
              bgcolor: '#0a0a0a',
              borderRight: '1px solid rgba(255,255,255,0.1)'
            }
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1} flexShrink={0}>
            <Typography variant="subtitle2" fontWeight={600}>Chats</Typography>
            <IconButton size="small" onClick={() => setSidebarOpen(false)}><X size={16} /></IconButton>
          </Box>
          {sidebarContent}
        </Drawer>
      )}

      {/* Main chat area */}
      <Box display="flex" flexDirection="column" flex={1} minWidth={0} minHeight={0}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} pb={1.5} px={isMobile ? 0 : 1.5} flexShrink={0}>
          {isMobile && (
            <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ color: 'text.secondary' }}>
              <PanelLeftOpen size={18} />
            </IconButton>
          )}
          <Bot size={22} strokeWidth={2} style={{ color: '#a78bfa' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Nexus
          </Typography>
          <Box flex={1} />
          {onClose && (
            <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              Close
            </Button>
          )}
        </Box>

        {/* Context banner */}
        {initialContext && (
          <Paper variant="outlined" sx={{ p: 1, mb: 1, mx: isMobile ? 0 : 1.5, borderColor: 'rgba(139,92,246,0.3)', bgcolor: 'rgba(139,92,246,0.05)', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Context: {initialContext.nodeType} in chapter "{initialContext.chapterName || '?'}"
            </Typography>
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ mb: 1, mx: isMobile ? 0 : 1.5, flexShrink: 0 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Messages */}
        <Box
          flex={1}
          overflow="auto"
          className="vr-scroll-y"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pb: 1,
            px: isMobile ? 0 : 1.5,
            minHeight: 0
          }}
        >
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && messages.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1} py={6}>
              <Bot size={40} style={{ color: 'rgba(167,139,250,0.3)' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Hi! I'm Nexus — your AI project expert.<br />
                Ask a question and I'll search the knowledge base.
              </Typography>
            </Box>
          )}

          {messages.map((msg, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Paper
                sx={{
                  p: 1.5,
                  maxWidth: '85%',
                  bgcolor: msg.role === 'user' ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.04)',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: msg.role === 'user' ? 'rgba(139,92,246,0.2)' : 'divider'
                }}
              >
                {msg.role === 'user' && msg.senderUsername && (
                  <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, display: 'block', fontSize: '0.7rem', mb: 0.5 }}>
                    @{msg.senderUsername}
                  </Typography>
                )}
                {msg.role === 'assistant' && msg.toolCalls && (
                  <NexusToolCallLog toolCalls={msg.toolCalls} />
                )}
                {msg.role === 'assistant' ? (
                  <ChatMarkdown content={msg.content} />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                    }}
                  >
                    {msg.content}
                  </Typography>
                )}
                {msg.widgets && <NexusWidgets widgets={msg.widgets} teamId={teamId} />}
              </Paper>
              <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.25, fontSize: '0.65rem' }}>
                {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' }) : ''}
              </Typography>
            </Box>
          ))}

          {sending && (
            <Box display="flex" alignItems="center" gap={1} pl={1}>
              <CircularProgress size={16} sx={{ color: '#a78bfa' }} />
              <Typography variant="caption" color="text.secondary">Nexus is thinking...</Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box display="flex" gap={1} pt={1} px={isMobile ? 0 : 1.5} flexShrink={0} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask Nexus..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem'
              }
            }}
          />
          <IconButton
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            sx={{
              bgcolor: 'rgba(139,92,246,0.15)',
              color: '#a78bfa',
              '&:hover': { bgcolor: 'rgba(139,92,246,0.25)' },
              '&.Mui-disabled': { color: 'text.disabled' }
            }}
          >
            <Send size={18} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
